import { useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { CalendarRange, ChevronLeft, ChevronRight, X } from "lucide-react";

/**
 * Datas em ISO local (yyyy-MM-dd). Nada de `toISOString()`: ele converte para
 * UTC e, no fuso de Brasília, depois das 21h devolveria o dia seguinte.
 */
const chaveISO = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

/** Também evita UTC: `new Date("2026-08-01")` cai na véspera aqui. */
const deISO = (iso: string): Date => {
  const [ano, mes, dia] = iso.split("-").map(Number);
  return new Date(ano, mes - 1, dia);
};

const hoje = () => {
  const agora = new Date();
  return new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
};

const curto = (iso: string) =>
  deISO(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });

const completo = (iso: string) => deISO(iso).toLocaleDateString("pt-BR");

const DIAS_SEMANA = ["D", "S", "T", "Q", "Q", "S", "S"];

/** Seis semanas a partir do domingo anterior ao dia 1º: grade de altura fixa. */
const gradeDoMes = (referencia: Date): Date[] => {
  const primeiro = new Date(referencia.getFullYear(), referencia.getMonth(), 1);
  const inicio = new Date(primeiro);
  inicio.setDate(1 - primeiro.getDay());

  return Array.from({ length: 42 }, (_, i) => {
    const dia = new Date(inicio);
    dia.setDate(inicio.getDate() + i);
    return dia;
  });
};

interface Atalho {
  rotulo: string;
  calcular: () => { inicio: string; fim: string };
}

const ATALHOS: Atalho[] = [
  {
    rotulo: "Últimos 7 dias",
    calcular: () => {
      const fim = hoje();
      const inicio = new Date(fim);
      inicio.setDate(fim.getDate() - 6);
      return { inicio: chaveISO(inicio), fim: chaveISO(fim) };
    },
  },
  {
    rotulo: "Últimos 30 dias",
    calcular: () => {
      const fim = hoje();
      const inicio = new Date(fim);
      inicio.setDate(fim.getDate() - 29);
      return { inicio: chaveISO(inicio), fim: chaveISO(fim) };
    },
  },
  {
    rotulo: "Este mês",
    calcular: () => {
      const agora = hoje();
      return {
        inicio: chaveISO(new Date(agora.getFullYear(), agora.getMonth(), 1)),
        fim: chaveISO(agora),
      };
    },
  },
  {
    rotulo: "Mês passado",
    calcular: () => {
      const agora = hoje();
      const primeiro = new Date(agora.getFullYear(), agora.getMonth() - 1, 1);
      const ultimo = new Date(agora.getFullYear(), agora.getMonth(), 0);
      return { inicio: chaveISO(primeiro), fim: chaveISO(ultimo) };
    },
  },
  {
    rotulo: "Este ano",
    calcular: () => {
      const agora = hoje();
      return {
        inicio: chaveISO(new Date(agora.getFullYear(), 0, 1)),
        fim: chaveISO(agora),
      };
    },
  },
];

interface CampoPeriodoProps {
  inicio?: string;
  fim?: string;
  aoMudar: (periodo: { inicio?: string; fim?: string }) => void;
  rotulo?: string;
  /** Última data selecionável. Padrão: hoje — não se abre chamado no futuro. */
  maximo?: string;
}

/**
 * Intervalo de datas num calendário próprio. O `input type="date"` nativo
 * resolvia o dado, mas cada navegador desenha o seu, e o do Chrome não tem nada
 * a ver com o resto da tela — além de pedir dois campos para o que é uma
 * escolha só. Aqui o intervalo é uma coisa: primeiro clique abre, segundo
 * fecha, e os atalhos cobrem os recortes que se pede quase sempre.
 */
export const CampoPeriodo = ({
  inicio,
  fim,
  aoMudar,
  rotulo = "Período de abertura",
  maximo,
}: CampoPeriodoProps) => {
  const limite = maximo ? deISO(maximo) : hoje();
  const temPeriodo = Boolean(inicio || fim);

  const [mes, setMes] = useState<Date>(() => {
    const base = inicio ? deISO(inicio) : hoje();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  // Enquanto só a primeira ponta foi escolhida, o hover mostra o intervalo que
  // sairia do próximo clique.
  const [sobrevoado, setSobrevoado] = useState<string | null>(null);

  const escolherDia = (iso: string) => {
    const meio = inicio && !fim;
    if (!meio) {
      aoMudar({ inicio: iso, fim: undefined });
      setSobrevoado(null);
      return;
    }
    // Clique anterior à abertura vira a nova abertura, em vez de recusar.
    if (iso < inicio!) aoMudar({ inicio: iso, fim: inicio });
    else aoMudar({ inicio, fim: iso });
    setSobrevoado(null);
  };

  const fimEfetivo = fim ?? (inicio && sobrevoado && sobrevoado > inicio ? sobrevoado : undefined);

  const texto = inicio
    ? fim
      ? `${curto(inicio)} – ${completo(fim)}`
      : `a partir de ${completo(inicio)}`
    : fim
      ? `até ${completo(fim)}`
      : "Qualquer data";

  return (
    <div>
      <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
        <CalendarRange className="mr-1 -mt-0.5 inline h-3.5 w-3.5" />
        {rotulo}
      </span>

      <Popover className="relative">
        <div className="flex items-center gap-1">
          <PopoverButton
            className={[
              "inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1",
              temPeriodo
                ? "border-blue-600 bg-blue-50 font-medium text-blue-700"
                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50",
            ].join(" ")}
          >
            <CalendarRange className="h-4 w-4" />
            {texto}
          </PopoverButton>

          {temPeriodo && (
            <button
              type="button"
              onClick={() => aoMudar({ inicio: undefined, fim: undefined })}
              aria-label="Limpar período"
              title="Limpar período"
              className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <PopoverPanel
          anchor={{ to: "bottom start", gap: 8 }}
          className="z-30 flex rounded-xl border border-gray-200 bg-white shadow-xl"
        >
          <div className="w-40 space-y-0.5 border-r border-gray-100 p-2">
            {ATALHOS.map((atalho) => (
              <button
                key={atalho.rotulo}
                type="button"
                onClick={() => {
                  const periodo = atalho.calcular();
                  aoMudar(periodo);
                  setMes(
                    new Date(
                      deISO(periodo.inicio).getFullYear(),
                      deISO(periodo.inicio).getMonth(),
                      1
                    )
                  );
                }}
                className="block w-full rounded-lg px-2.5 py-1.5 text-left text-sm text-gray-600 transition-colors hover:bg-blue-50 hover:text-blue-700"
              >
                {atalho.rotulo}
              </button>
            ))}
          </div>

          <div className="p-3">
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))}
                aria-label="Mês anterior"
                className="rounded-lg p-1 text-gray-500 transition-colors hover:bg-gray-100"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm font-semibold text-gray-800 first-letter:uppercase">
                {mes.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}
              </span>
              <button
                type="button"
                onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))}
                aria-label="Próximo mês"
                className="rounded-lg p-1 text-gray-500 transition-colors hover:bg-gray-100"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {DIAS_SEMANA.map((dia, i) => (
                <span
                  key={`${dia}-${i}`}
                  className="py-1 text-center text-xs font-medium text-gray-400"
                >
                  {dia}
                </span>
              ))}

              {gradeDoMes(mes).map((dia) => {
                const iso = chaveISO(dia);
                const doMes = dia.getMonth() === mes.getMonth();
                const futuro = dia > limite;
                const ehInicio = iso === inicio;
                const ehFim = iso === fim;
                const dentro =
                  inicio && fimEfetivo && iso > inicio && iso < fimEfetivo;

                return (
                  <button
                    key={iso}
                    type="button"
                    disabled={futuro}
                    onClick={() => escolherDia(iso)}
                    onMouseEnter={() => setSobrevoado(iso)}
                    aria-label={completo(iso)}
                    className={[
                      "h-8 w-8 rounded-lg text-sm transition-colors",
                      ehInicio || ehFim
                        ? "bg-blue-600 font-semibold text-white"
                        : dentro
                          ? "bg-blue-50 text-blue-700"
                          : doMes
                            ? "text-gray-700 hover:bg-gray-100"
                            : "text-gray-300 hover:bg-gray-50",
                      futuro ? "cursor-not-allowed text-gray-200 hover:bg-transparent" : "",
                    ].join(" ")}
                  >
                    {dia.getDate()}
                  </button>
                );
              })}
            </div>

            <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-2">
              <button
                type="button"
                onClick={() => aoMudar({ inicio: undefined, fim: undefined })}
                className="rounded-lg px-2 py-1 text-sm text-gray-500 transition-colors hover:bg-gray-100"
              >
                Limpar
              </button>
              {inicio && !fim && (
                <span className="text-xs text-gray-400">
                  Escolha a data final
                </span>
              )}
            </div>
          </div>
        </PopoverPanel>
      </Popover>
    </div>
  );
};

export default CampoPeriodo;
